import React, { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, gql, useMutation } from "@apollo/client";
import StarekContext from "../../context/StarekContext";
import EntityLoadingState from "../../components/EntityLoadingState";
import EntityEmptyState from "../../components/EntityEmptyState";
import { EMPTY_MESSAGES } from "../../constants/emptyStateMessages";
import ZoneSidebar from "./ZoneSidebar";
import ZoneHierarchicalView from "./ZoneHierarchicalView";
import ZoneListingView from "./ZoneListingView";
import ConfirmModal from "../../components/modals/ConfirmModal";
import EntityOperationBar from "../../components/EntityOperationBar";
import { usePermissions } from "../../hooks/usePermissions";
import { buildListRoute, parseRouteEntityId } from "../../utils";
import { softDeleteData, restoreData } from "../../utils/softDelete";
import "../../starek.css";

// UI Strings
const UI_STRINGS = {
    PAGE_TITLE: "Zones",
    SEARCH_PLACEHOLDER: "Search zones…",
    VIEW_MODE_LIST: "List View",
    VIEW_MODE_DEFAULT: "Default View",
    ENTITY_SINGULAR: "Zone",
    ENTITY_PLURAL: "Zones",
    DELETE_TITLE_SINGLE: (name) => `Delete Zone ${name}?`,
    DELETE_TITLE_BULK: (count) =>
        `Delete ${count} Zone${count !== 1 ? "s" : ""}?`,
    DELETE_MESSAGE_SINGLE: (name) => `You are about to delete zone "${name}".

This action cannot be undone. All child zones and installed devices associated with this zone will also be deleted.

Are you sure you wish to continue?`,
    DELETE_MESSAGE_BULK: (count) => `You are about to delete ${count} zone(s).

This action cannot be undone. All child zones and installed devices associated with these zones will also be deleted.

Are you sure you wish to continue?`,
    DELETE_CONFIRM: "Delete",
    DELETE_CANCEL: "Cancel",
    TOTAL_SELECTED: (count) => `Total zones selected: ${count}`,
    ERROR_NO_USER:
        "No current user in context; cannot set created_by/updated_by.",
    NOT_FOUND: "Zone not found",
};

function Zones() {
    const starekContext = useContext(StarekContext);
    const navigate = useNavigate();
    const { id: routeId } = useParams();
    const routeZoneId = parseRouteEntityId(routeId);
    const [searchParams, setSearchParams] = useSearchParams();

    const { can } = usePermissions();
    const canWrite   = can("zone", "WRITE");
    const canDestroy = can("zone", "DESTROY");

    const GET_ZONE_LIST_FOR_PLANT = gql`
        query GetZones($where: ZoneWhereInput!) {
            zones(where: $where) {
                id
                parent_id
                name
                description
                is_active
                deleted_at
                created_at
                updated_at
                created_by {
                    id
                    name
                }
                updated_by {
                    id
                    name
                }
                installed_devices {
                    id
                    name
                }
                _count {
                    installed_devices
                }
            }
        }
    `;

    const UPDATE_MANY_ZONE = gql`
        mutation UpdateManyZone($data: ZoneUpdateManyMutationInput!, $where: ZoneWhereInput) {
            updateManyZone(data: $data, where: $where) {
                count
            }
        }
    `;

    const GET_DEVICES_IN_PLANT = gql`
        query GetDevicesForPlant($where: ShadowDeviceWhereInput) {
            shadowDevices(where: $where) {
                id
                name
            }
        }
    `;

    const DELETE_MANY_ZONES = gql`
        mutation DeleteManyZone($ids: [Int!]!) {
            deleteManyZone(where: { id: { in: $ids } }) {
                count
            }
        }
    `;

    const DELETE_ONE_ZONE = gql`
        mutation DeleteOneZone($id: Int!) {
            deleteOneZone(where: { id: $id }) {
                id
            }
        }
    `;

    const CREATE_ONE_ZONE = gql`
        mutation CreateOneZone($data: ZoneCreateInput!) {
            createOneZone(data: $data) {
                id
                name
                description
                parent {
                    id
                    name
                }
                installed_devices {
                    id
                    name
                }
            }
        }
    `;

    const UPDATE_ONE_ZONE = gql`
        mutation UpdateOneZone(
            $where: ZoneWhereUniqueInput!
            $data: ZoneUpdateInput!
        ) {
            updateOneZone(where: $where, data: $data) {
                id
                name
                description
                parent {
                    id
                    name
                }
                installed_devices {
                    id
                    name
                }
            }
        }
    `;

    const [showInactive, setShowInactive] = useState(false);

    const zoneQueryWhere = {
        plant_id: { equals: starekContext.lastUsedPlant.id },
        ...(showInactive ? {} : { is_active: { equals: true } }),
    };

    const { data, loading, error, refetch, client } = useQuery(
        GET_ZONE_LIST_FOR_PLANT,
        {
            client: starekContext.engineClient,
            variables: { where: zoneQueryWhere },
        },
    );

    const [createOneZone] = useMutation(CREATE_ONE_ZONE, {
        client: starekContext.engineClient,
    });
    const [updateOneZone] = useMutation(UPDATE_ONE_ZONE, {
        client: starekContext.engineClient,
    });

    const [items, setItems] = useState([]);
    const [showAddZoneDrawer, setShowAddZoneDrawer] = useState(false);
    const [viewMode, setViewMode] = useState("tree");
    const [filterText, setFilterText] = useState("");
    const [routeEditZone, setRouteEditZone] = useState(null);

    useEffect(() => {
        const viewParam = searchParams.get("view");
        if (viewParam && (viewParam === "tree" || viewParam === "list")) {
            setViewMode(viewParam);
        }
    }, [searchParams]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [toggleCleared, setToggleCleared] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteDone, setDeleteDone] = useState(0);
    const [confirmCfg, setConfirmCfg] = useState({ open: false });
    const [deleteManyZone] = useMutation(DELETE_MANY_ZONES);
    const [deleteOneZone] = useMutation(DELETE_ONE_ZONE);
    const [updateManyZone] = useMutation(UPDATE_MANY_ZONE, { client: starekContext.engineClient });
    const [togglingActiveId, setTogglingActiveId] = useState(null);
    const zonesFromQuery = useMemo(() => data?.zones ?? [], [data?.zones]);

    useEffect(() => {
        if (error) starekContext.showError(error);
    }, [error, starekContext]);

    useEffect(() => {
        if (!data) return;
        setItems(data.zones);
    }, [data]);

    useEffect(() => {
        if (!routeId) {
            setRouteEditZone(null);
            return;
        }

        if (loading) return;

        const listRoute = buildListRoute("/zones", searchParams);

        if (routeZoneId == null) {
            starekContext.showError(UI_STRINGS.NOT_FOUND);
            setRouteEditZone(null);
            navigate(listRoute, { replace: true });
            return;
        }

        const zoneToEdit = zonesFromQuery.find(
            (zone) => Number(zone?.id) === routeZoneId,
        );

        if (!zoneToEdit) {
            starekContext.showError(UI_STRINGS.NOT_FOUND);
            setRouteEditZone(null);
            navigate(listRoute, { replace: true });
            return;
        }

        setRouteEditZone(zoneToEdit);
        setSelectedRows((current) =>
            current.length === 1 && Number(current[0]?.id) === routeZoneId
                ? current
                : [zoneToEdit],
        );
        setShowAddZoneDrawer(true);
    }, [
        routeId,
        routeZoneId,
        loading,
        zonesFromQuery,
        searchParams,
        navigate,
        starekContext,
    ]);

    // Helper function to get all descendant IDs of a zone
    const getDescendantIds = (zoneId, zones) => {
        const descendants = new Set();
        const findChildren = (parentId) => {
            zones.forEach((z) => {
                if (z.parent_id === parentId && !descendants.has(z.id)) {
                    descendants.add(z.id);
                    findChildren(z.id);
                }
            });
        };
        findChildren(zoneId);
        return descendants;
    };

    const parentOptions = useMemo(() => {
        return items.map((z) => ({ value: z.id, label: z.name }));
    }, [items]);

    const getFilteredParentOptions = (currentZone) => {
        if (!currentZone?.id) return parentOptions;

        const excludeIds = getDescendantIds(currentZone.id, items);
        excludeIds.add(currentZone.id);

        return parentOptions.filter((opt) => !excludeIds.has(opt.value));
    };

    const {
        data: shadowData,
        loading: shadowLoading,
        error: shadowError,
    } = useQuery(GET_DEVICES_IN_PLANT, {
        client: starekContext.engineClient,
        variables: {
            where: {
                plant: {
                    is: {
                        id: {
                            equals: starekContext.lastUsedPlant?.id,
                        },
                    },
                },
            },
        },
        fetchPolicy: "cache-first",
    });
    useEffect(() => {
        if (shadowError) starekContext.showError(shadowError);
    }, [shadowError, starekContext]);

    const deviceOptions = useMemo(() => {
        const list = shadowData?.shadowDevices ?? [];
        return list.map((d) => ({ value: d.id, label: d.name || d.id }));
    }, [shadowData]);

    const openConfirm = (cfg) => setConfirmCfg({ open: true, ...cfg });
    const closeConfirm = () => setConfirmCfg({ open: false });

    const handleSaveZone = async (zonePayload) => {
        try {
            const currentUserId =
                starekContext?.currentUser?.id ??
                starekContext?.user?.id ??
                starekContext?.me?.id;

            if (!currentUserId) {
                console.error(
                    "Missing currentUserId for created_by/updated_by connect",
                );
                throw new Error(UI_STRINGS.ERROR_NO_USER);
            }
            const base = {
                name: zonePayload.name,
                description: zonePayload.description || "",
                plant: { connect: { id: Number(zonePayload.plant_id) } },
            };

            if (!zonePayload.id) {
                const data = {
                    ...base,
                    is_active: zonePayload.is_active ?? true,
                    ...(zonePayload.parent_id != null
                        ? {
                              parent: {
                                  connect: {
                                      id: Number(zonePayload.parent_id),
                                  },
                              },
                          }
                        : {}),
                    ...(Array.isArray(zonePayload.selected_device_ids) &&
                    zonePayload.selected_device_ids.length > 0
                        ? {
                              installed_devices: {
                                  connect: zonePayload.selected_device_ids.map(
                                      (id) => ({ id }),
                                  ),
                              },
                          }
                        : {}),
                    created_by: { connect: { id: Number(currentUserId) } },
                    updated_by: { connect: { id: Number(currentUserId) } },
                };
                await createOneZone({ variables: { data } });
            } else {
                const data = {
                    name: { set: base.name },
                    description: { set: base.description },
                    plant: base.plant,
                    ...(zonePayload.parent_id === null
                        ? { parent: { disconnect: {} } }
                        : zonePayload.parent_id !== undefined
                          ? {
                                parent: {
                                    connect: {
                                        id: Number(zonePayload.parent_id),
                                    },
                                },
                            }
                          : {}),
                    ...(Array.isArray(zonePayload.selected_device_ids)
                        ? {
                              installed_devices: {
                                  set: zonePayload.selected_device_ids.map(
                                      (id) => ({ id }),
                                  ),
                              },
                          }
                        : {}),
                    updated_by: { connect: { id: Number(currentUserId) } },
                };
                await updateOneZone({
                    variables: {
                        where: { id: Number(zonePayload.id) },
                        data,
                    },
                });
            }
            await handleDrawerClose(true, false);
            starekContext.showSuccess(
                zonePayload.id
                    ? `Zone "${zonePayload.name}" updated successfully`
                    : `Zone "${zonePayload.name}" created successfully`,
            );
        } catch (error) {
            starekContext.showError(error);
            throw error;
        }
    };

    const handleAdd = (parentZoneId = null) => {
        setRouteEditZone(null);
        if (parentZoneId === null) {
            setSelectedRows([]);
        } else {
            const parentZone = items.find((z) => z.id === parentZoneId);
            if (parentZone) {
                setSelectedRows([
                    {
                        parent: { id: parentZoneId, name: parentZone.name },
                        parent_id: parentZoneId,
                    },
                ]);
            }
        }
        setShowAddZoneDrawer(true);
    };

    const handleRowClicked = (row) => {
        navigate(buildListRoute(`/zones/${row.id}`, searchParams));
    };

    const handleEdit = (zoneIdOrNull = null) => {
        setRouteEditZone(null);
        if (zoneIdOrNull) {
            navigate(buildListRoute(`/zones/${zoneIdOrNull}`, searchParams));
        } else if (selectedRows.length === 1) {
            navigate(buildListRoute(`/zones/${selectedRows[0].id}`, searchParams));
        }
    };

    const handleDelete = (zones) => {
        if (!zones || zones.length === 0 || isDeleting) return;

        const isSingle = zones.length === 1;
        const title = isSingle
            ? UI_STRINGS.DELETE_TITLE_SINGLE(zones[0].name)
            : UI_STRINGS.DELETE_TITLE_BULK(zones.length);

        const message = isSingle
            ? UI_STRINGS.DELETE_MESSAGE_SINGLE(zones[0].name)
            : UI_STRINGS.DELETE_MESSAGE_BULK(zones.length);

        openConfirm({
            title: title,
            content: (
                <>
                    <div
                        className="text-dark"
                        style={{ whiteSpace: "pre-wrap" }}
                    >
                        {message}
                    </div>
                    <p className="mt-4 text-dark fw-bold">
                        {UI_STRINGS.TOTAL_SELECTED(zones.length)}&nbsp;
                        <span className="badge bg-info">{zones.length}</span>
                    </p>
                </>
            ),
            confirmText: UI_STRINGS.DELETE_CONFIRM,
            cancelText: UI_STRINGS.DELETE_CANCEL,
            type: "danger",
            onConfirm: async () => {
                setIsDeleting(true);
                setDeleteDone(0);

                const ids = zones.map((z) => z.id);

                try {
                    // Try batch delete first
                    await deleteManyZone({ variables: { ids } });
                    setDeleteDone(ids.length);
                    starekContext.showSuccess(
                        `Successfully deleted ${ids.length} zone(s)`,
                    );
                } catch {
                    // Fallback to individual deletes
                    for (const id of ids) {
                        try {
                            await deleteOneZone({ variables: { id } });
                            setDeleteDone((d) => d + 1);
                        } catch (err) {
                            starekContext.showError?.(err);
                            break;
                        }
                    }
                } finally {
                    setIsDeleting(false);
                    setSelectedRows([]);
                    setToggleCleared((p) => !p);
                    await refetch();
                    closeConfirm();
                }
            },
            onClose: closeConfirm,
        });
    };

    const handleDrawerClose = async (itemModified, itemDeleted) => {
        setShowAddZoneDrawer(false);
        setRouteEditZone(null);
        setSelectedRows([]);
        setToggleCleared((p) => !p);

        if (routeId || itemDeleted) {
            navigate(buildListRoute("/zones", searchParams), {
                replace: true,
            });
        }

        await new Promise((resolve) => setTimeout(resolve, 0));
        await refetch();
    };

    const handleToggleActive = async (zones, makeActive) => {
        const userId = starekContext?.currentUser?.id ?? starekContext?.user?.id;
        const ids = zones.map((z) => z.id);
        try {
            await updateManyZone({
                variables: {
                    data: makeActive ? restoreData() : softDeleteData(),
                    where: { id: { in: ids } },
                },
            });
            starekContext.showSuccess(makeActive ? `Restored ${ids.length} zone(s)` : `Deactivated ${ids.length} zone(s)`);
            setSelectedRows([]);
            setToggleCleared((p) => !p);
            await refetch();
        } catch (err) {
            starekContext.showError(err);
        }
    };

    const handleToggleActiveSingle = async (zone) => {
        const userId = starekContext?.currentUser?.id ?? starekContext?.user?.id;
        setTogglingActiveId(zone.id);
        try {
            await updateManyZone({
                variables: {
                    data: zone.is_active ? softDeleteData() : restoreData(),
                    where: { id: { in: [zone.id] } },
                },
            });
            await refetch();
        } catch (err) {
            starekContext.showError(err);
        } finally {
            setTogglingActiveId(null);
        }
    };

    const zoneExtendedActions = selectedRows.length > 0 ? [
        {
            label: "Deactivate",
            icon: "fa-ban",
            danger: true,
            disabled: selectedRows.every((r) => !r.is_active),
            onClick: (rows) => handleToggleActive(rows.filter((r) => r.is_active), false),
        },
        {
            label: "Restore",
            icon: "fa-undo",
            disabled: selectedRows.every((r) => r.is_active),
            onClick: (rows) => handleToggleActive(rows.filter((r) => !r.is_active), true),
        },
    ] : [];

    if (loading || shadowLoading) return <EntityLoadingState icon="fa-bullseye" />;

    const activeZone = routeEditZone || selectedRows[0] || null;

    return (
        <div className="container-fluid">
            <div className="font-weight-bold mb-3">{UI_STRINGS.PAGE_TITLE}</div>

            <EntityOperationBar
                entity={{
                    singular: UI_STRINGS.ENTITY_SINGULAR,
                    plural: UI_STRINGS.ENTITY_PLURAL,
                }}
                allowSearch={true}
                allowAdd={canWrite}
                allowDelete={canDestroy}
                allowEdit={canWrite}
                allowExtendedActions={canWrite && selectedRows.length > 0}
                extendedActions={zoneExtendedActions}
                allowShowInactive={true}
                showInactive={showInactive}
                onShowInactiveChange={(v) => { setShowInactive(v); setSelectedRows([]); }}
                defaultView={viewMode}
                selectedRows={selectedRows}
                isDeleting={isDeleting}
                deleteDone={deleteDone}
                searchPlaceholder={UI_STRINGS.SEARCH_PLACEHOLDER}
                onSearch={(text) => setFilterText(text)}
                onAdd={() => handleAdd()}
                onViewChange={(mode) => {
                    setViewMode(mode);
                    setSearchParams({ view: mode });
                }}
                onDelete={() => handleDelete(selectedRows)}
                onEdit={() => handleEdit()}
            >
                <EntityOperationBar.View
                    mode="tree"
                    icon="fa-sitemap"
                    tooltip={UI_STRINGS.VIEW_MODE_DEFAULT}
                >
                    <div style={{ height: "700px", width: "100%" }}>
                        <ZoneHierarchicalView
                            zones={items}
                            onAddZone={handleAdd}
                            onEditZone={handleEdit}
                            selectedRows={selectedRows}
                            onSelectedRowsChange={({ selectedRows }) =>
                                setSelectedRows(selectedRows)
                            }
                            onToggleActive={handleToggleActiveSingle}
                            togglingActiveId={togglingActiveId}
                        />
                    </div>
                </EntityOperationBar.View>

                <EntityOperationBar.View
                    mode="list"
                    icon="fa-list-ul"
                    tooltip={UI_STRINGS.VIEW_MODE_LIST}
                >
                    <ZoneListingView
                        items={items}
                        filterText={filterText}
                        selectedRows={selectedRows}
                        toggleCleared={toggleCleared}
                        onSelectedRowsChange={({ selectedRows }) =>
                            setSelectedRows(selectedRows)
                        }
                        UI_STRINGS={UI_STRINGS}
                        noDataComponent={<EntityEmptyState icon="fa-bullseye" entityName="Zone" messages={EMPTY_MESSAGES.zones} isFiltered={!!filterText.trim()} onAdd={() => handleAdd()} />}
                        onRowClicked={handleRowClicked}
                    />
                </EntityOperationBar.View>
            </EntityOperationBar>

            {showAddZoneDrawer && (
                <ZoneSidebar
                    show={true}
                    item={activeZone}
                    plantId={starekContext.lastUsedPlant.id}
                    parentOptions={getFilteredParentOptions(
                        activeZone,
                    )}
                    deviceOptions={deviceOptions}
                    onSave={handleSaveZone}
                    onDelete={(zone) => handleDelete([zone])}
                    onDiscard={() => handleDrawerClose(false, false)}
                    onToggleActive={handleToggleActiveSingle}
                />
            )}

            <ConfirmModal
                open={confirmCfg.open}
                type={confirmCfg.type}
                title={confirmCfg.title}
                confirmText={confirmCfg.confirmText}
                cancelText={confirmCfg.cancelText}
                loading={isDeleting}
                onConfirm={confirmCfg.onConfirm}
                onClose={confirmCfg.onClose}
            >
                {confirmCfg.content}
            </ConfirmModal>
        </div>
    );
}

export { Zones };
